/**
 * emailService.js – E2I Solutions
 * ─────────────────────────────────────────────────────────────────────────────
 * Couche d'abstraction pour l'envoi d'emails.
 * En mode DEV : simulation console (aucun email réel envoyé).
 * En production : brancher sur l'endpoint WordPress ou un service tiers.
 *
 * INTÉGRATION PRODUCTION (choisir une option) :
 *
 * Option A – Endpoint WordPress personnalisé (recommandé)
 *   1. Installer le plugin WordPress inclus dans /wordpress/
 *   2. Mettre DEV_MODE = false et renseigner WP_API_ENDPOINT
 *
 * Option B – Service tiers (SendGrid, Mailchimp Transactional, Brevo...)
 *   1. Créer un compte, récupérer la clé API
 *   2. Configurer THIRD_PARTY_CONFIG ci-dessous
 *
 * Option C – PHP mailer direct (via fetch vers un script PHP sur OVH)
 *   1. Déposer le script PHP (voir /wordpress/mailer.php)
 *   2. Renseigner PHP_MAILER_URL
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ─── Configuration ────────────────────────────────────────────────────────────

const DEV_MODE = false // ← Passer à true pour désactiver les envois réels

const CONFIG = {
  // Option A : WordPress REST API
  wpApiEndpoint: '/wp-json/e2i-devis/v1/',

  // Option B : Service tiers (ex: Brevo/SendInBlue)
  thirdParty: {
    provider: 'brevo', // 'brevo' | 'sendgrid' | 'mailchimp'
    apiKey: 'VOTRE_CLE_API_ICI',
    fromEmail: 'noreply@e2i-solutions.com',
    fromName: 'E2I Solutions',
  },

  // Option C : Script PHP direct (OVH)
  phpMailerUrl: 'https://votre-site.fr/wp-content/plugins/e2i-simulateur/mailer.php',

  // Email de réception des notifications (côté E2I)
  notificationEmail: 'contact@e2i-solutions.com',
  notificationEmailName: 'E2I Solutions',
}

// ─── Génération de token ──────────────────────────────────────────────────────

/** Génère un token de confirmation aléatoire */
export function generateToken() {
  const array = new Uint8Array(24)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
}

// ─── Envoi de l'email de confirmation prospect ────────────────────────────────

/**
 * Envoie un email de confirmation au prospect pour valider son adresse.
 *
 * @param {{ email: string, prenom: string, nom: string, token: string }} params
 * @returns {Promise<{ success: boolean, messageId?: string, error?: string }>}
 */
export async function sendConfirmationEmail({ email, prenom, nom, token }) {
  if (DEV_MODE) {
    console.group('[E2I DEV] Email de confirmation')
    console.log('Destinataire :', `${prenom} ${nom} <${email}>`)
    console.log('Token        :', token)
    console.log('Lien simulé  :', `${window.location.origin}?e2i_token=${token}`)
    console.groupEnd()
    // Simule un léger délai réseau
    await sleep(800)
    return { success: true, messageId: `dev_confirm_${Date.now()}` }
  }

  // ── Production : appel WordPress REST API ───────────────────────────────
  try {
    const res = await fetch(`${CONFIG.wpApiEndpoint}send-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, prenom, nom, token }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  } catch (err) {
    console.error('[E2I] Erreur envoi email confirmation :', err)
    return { success: false, error: err.message }
  }
}

// ─── Vérification du token ────────────────────────────────────────────────────

/**
 * Vérifie la validité d'un token de confirmation.
 * En dev : accepte n'importe quel token.
 *
 * @param {string} token
 * @returns {Promise<{ valid: boolean }>}
 */
export async function verifyToken(token) {
  if (DEV_MODE) {
    await sleep(400)
    return { valid: Boolean(token) }
  }

  try {
    const res = await fetch(`${CONFIG.wpApiEndpoint}verify-token?token=${token}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  } catch (err) {
    console.error('[E2I] Erreur vérification token :', err)
    return { valid: false }
  }
}

// ─── Notification interne E2I ─────────────────────────────────────────────────

/**
 * Envoie une notification à E2I Solutions avec les détails du projet et du contact.
 *
 * @param {{ contact: Object, config: Object, estimate: Object }} params
 * @returns {Promise<{ success: boolean }>}
 */
export async function sendNotificationToE2I({ contact, config, estimate }) {
  if (DEV_MODE) {
    console.group('[E2I DEV] Notification interne')
    console.log('Contact :', contact)
    console.log('Projet  :', config)
    console.log('Estimate:', estimate)
    console.groupEnd()
    await sleep(300)
    return { success: true }
  }

  try {
    const res = await fetch(`${CONFIG.wpApiEndpoint}notify-e2i`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact, config, estimate }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  } catch (err) {
    console.error('[E2I] Erreur notification interne :', err)
    return { success: false }
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

/** Vérifie si on est en mode développement */
export const isDevMode = () => DEV_MODE
